import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  Bell,
  CheckCircle2,
  ClipboardList,
  Clock3,
  ListTodo,
  MessageSquareText,
  UserRoundCheck,
  Users,
  X
} from 'lucide-react';
import { useAuth } from '../../core/auth/AuthProvider';
import { db } from '../../core/firebase/firebase';
import {
  hasRegisteredNotificationToken,
  requestBrowserNotifications
} from '../../core/notifications/browserNotifications';
import PageHeader from '../../components/ui/PageHeader';
import StatCard from '../../components/ui/StatCard';

const INITIAL_DATA = {
  registrations: [],
  complaints: [],
  parents: [],
  tasks: []
};

function timestampValue(value) {
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') return Date.parse(value) || 0;
  return 0;
}

function formatDate(value) {
  const milliseconds = timestampValue(value);
  if (!milliseconds) return 'Date non définie';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
  }).format(new Date(milliseconds));
}

const REGISTRATION_LABELS = { nouvelle:'Nouvelle', new:'Nouvelle', to_contact:'À contacter', called:'Appelée', contacted:'Contactée', visit_scheduled:'Visite programmée', accepted:'Acceptée', acceptee:'Acceptée', approved:'Acceptée', validee:'Acceptée', inscrite:'Acceptée', student_created:'Convertie', converted:'Convertie', completed:'Terminée', rejected:'Rejetée', rejetee:'Rejetée', cancelled:'Annulée' };
function registrationStatus(item) { return String(item.status || 'nouvelle').trim().toLowerCase(); }
function registrationLabel(item) { const status = registrationStatus(item); return REGISTRATION_LABELS[status] || status; }
function guardianName(item) { return item.guardian?.fullName || item.parentName || item.parentFullname || item.parentNom || item.responsibleParent?.fullName || 'Parent non renseigné'; }
function childName(item) { return item.familyName || item.children?.[0]?.fullName || item.studentName || item.studentFullname || item.fullname || 'Famille non renseignée'; }
function requestedLevel(item) { return item.children?.[0]?.requestedLevel || item.level || item.classLevel || item.requestedLevel || 'Niveau non renseigné'; }

function isRegistrationActive(item) {
  return !['student_created', 'converted', 'completed', 'rejected', 'rejetee', 'cancelled'].includes(registrationStatus(item));
}

function taskStatus(task) {
  const status = task.status || 'open';
  if (status === 'todo' || status === 'in_progress') return 'open';
  if (status === 'waiting_parent') return 'waiting';
  return status;
}

function isOverdue(task) {
  const due = timestampValue(task.dueDate);
  return taskStatus(task) !== 'done' && due > 0 && due < Date.now();
}

function currentRoles(profile) {
  const raw = profile?.adminRoles ?? profile?.adminRole ?? profile?.role ?? [];
  return (Array.isArray(raw) ? raw : [raw]).map((role) => role === 'admin' || role === 'administration' ? 'gerant' : role);
}

function DashboardList({ title, eyebrow, items, emptyText, icon: Icon, render }) {
  return <article className="panel">
    <div className="panel-heading">
      <div><p className="eyebrow">{eyebrow}</p><h2>{title}</h2></div>
      <span className="live-badge"><span/> Temps réel</span>
    </div>
    <div className="dashboard-list">
      {items.length === 0 && <div className="compact-empty"><CheckCircle2 size={22}/><span>{emptyText}</span></div>}
      {items.map((item) => (
        <div className="dashboard-list-item" key={item.id}>
          <div className="list-icon blue"><Icon size={18}/></div>
          {render(item)}
        </div>
      ))}
    </div>
  </article>;
}

function ManagerDashboard({ data, loading, firstName }) {
  const activeRegistrations = data.registrations.filter(isRegistrationActive);
  const openComplaints = data.complaints.filter((item) => !['done', 'resolved', 'closed', 'archivee', 'traitee', 'traitée', 'fermee', 'fermée'].includes(String(item.status || '').trim().toLowerCase()));
  const openTasks = data.tasks.filter((task) => taskStatus(task) !== 'done');
  const activeParents = data.parents.filter((parent) => !parent.status || parent.status === 'approved' || parent.status === 'active');

  return <>
    <PageHeader eyebrow="TABLEAU DE BORD GÉRANT" title={`Bonjour ${firstName} 👋`} description="Vue globale de l’activité de l’établissement."/>
    <section className="stats-grid">
      <StatCard label="Inscriptions à traiter" value={loading ? '…' : String(activeRegistrations.length)} hint="Demandes actives" tone="blue" to="/inscriptions?statut=nouvelle"/>
      <StatCard label="Réclamations ouvertes" value={loading ? '…' : String(openComplaints.length)} hint="À examiner" tone="rose" to="/communication?vue=reclamations"/>
      <StatCard label="Parents actifs" value={loading ? '…' : String(activeParents.length)} hint="Profils disponibles" tone="green" to="/parents"/>
      <StatCard label="Tâches ouvertes" value={loading ? '…' : String(openTasks.length)} hint="Tous responsables" tone="amber" to="/taches"/>
    </section>
    <section className="dashboard-grid">
      <DashboardList title="Demandes récentes" eyebrow="INSCRIPTIONS" items={activeRegistrations.slice(0, 5)} emptyText="Aucune demande active." icon={ClipboardList} render={(item) => <>
        <div className="list-copy"><strong>{childName(item)}</strong><span>{guardianName(item)} · {requestedLevel(item)}</span></div>
        <div className="list-meta"><span className={`status-chip status-${registrationStatus(item)}`}>{registrationLabel(item)}</span><small>{formatDate(item.createdAt)}</small></div>
      </>}/>
      <DashboardList title="Réclamations récentes" eyebrow="COMMUNICATION" items={openComplaints.slice(0, 5)} emptyText="Aucune réclamation ouverte." icon={MessageSquareText} render={(item) => <>
        <div className="list-copy"><strong>{item.subject || 'Sans objet'}</strong><span>{item.parentNom || item.parentName || 'Parent'} · {item.message || 'Aucun message'}</span></div>
        <div className="list-meta"><small>{formatDate(item.createdAt)}</small></div>
      </>}/>
    </section>
  </>;
}

function AgentDashboard({ data, loading, firstName, userId }) {
  const activeRegistrations = data.registrations.filter(isRegistrationActive);
  const newRegistrations = data.registrations.filter((item) => ['nouvelle','new','to_contact'].includes(registrationStatus(item)));
  const acceptedNotConverted = data.registrations.filter((item) => ['accepted', 'acceptee', 'approved', 'validee'].includes(registrationStatus(item)) && !item.parentId);
  const waitingActivation = data.parents.filter((parent) => ['pending_activation', 'credentials_ready', 'sms_pending'].includes(parent.activationStatus));
  const myTasks = data.tasks.filter((task) => taskStatus(task) !== 'done' && (!task.assignedToUid || task.assignedToUid === userId));
  const urgentTasks = myTasks.filter((task) => task.priority === 'urgent' || task.priority === 'high' || isOverdue(task));

  return <>
    <PageHeader eyebrow="ESPACE AGENT ADMINISTRATIF" title={`Bonjour ${firstName} 👋`} description="Voici les actions administratives à traiter aujourd’hui." actions={<div className="dashboard-actions"><Link className="button button-primary" to="/inscriptions">Traiter les inscriptions</Link><Link className="button button-secondary" to="/taches">Voir mes tâches</Link></div>}/>
    <section className="stats-grid">
      <StatCard label="Nouvelles demandes" value={loading ? '…' : String(newRegistrations.length)} hint="À examiner" tone="blue" to="/inscriptions?statut=nouvelle"/>
      <StatCard label="À convertir" value={loading ? '…' : String(acceptedNotConverted.length)} hint="Familles acceptées" tone="amber" to="/inscriptions?statut=inscrite"/>
      <StatCard label="Activations en attente" value={loading ? '…' : String(waitingActivation.length)} hint="Identifiants ou SMS" tone="green" to="/parents?activation=pending"/>
      <StatCard label="Tâches urgentes" value={loading ? '…' : String(urgentTasks.length)} hint="Prioritaires ou en retard" tone="rose" to="/taches?filtre=urgent"/>
    </section>
    <section className="agent-quick-grid">
      <Link to="/inscriptions" className="agent-quick-card"><ClipboardList/><span><strong>Inscriptions</strong><small>Examiner et valider les familles</small></span></Link>
      <Link to="/parents" className="agent-quick-card"><UserRoundCheck/><span><strong>Parents</strong><small>Préparer et suivre les comptes</small></span></Link>
      <Link to="/eleves" className="agent-quick-card"><Users/><span><strong>Élèves</strong><small>Consulter les dossiers liés</small></span></Link>
      <Link to="/taches" className="agent-quick-card"><ListTodo/><span><strong>Mes tâches</strong><small>SMS, appels et documents</small></span></Link>
    </section>
    <section className="dashboard-grid">
      <DashboardList title="Inscriptions à traiter" eyebrow="PRIORITÉ" items={activeRegistrations.slice(0, 6)} emptyText="Toutes les demandes sont traitées." icon={ClipboardList} render={(item) => <>
        <div className="list-copy"><strong>{childName(item)}</strong><span>{guardianName(item)} · {requestedLevel(item)}</span></div>
        <div className="list-meta"><span className={`status-chip status-${registrationStatus(item)}`}>{registrationLabel(item)}</span><small>{formatDate(item.createdAt)}</small></div>
      </>}/>
      <DashboardList title="Mes tâches ouvertes" eyebrow="ORGANISATION" items={myTasks.slice(0, 6)} emptyText="Aucune tâche ouverte." icon={Clock3} render={(item) => <>
        <div className="list-copy"><strong>{item.title || 'Tâche sans titre'}</strong><span>{item.description || item.type || 'Aucun détail'}</span></div>
        <div className="list-meta"><span className={`priority-chip priority-${item.priority || 'medium'}`}>{item.priority || 'moyenne'}</span><small>{formatDate(item.dueDate)}</small></div>
      </>}/>
    </section>
  </>;
}

export default function DashboardPage() {
  const { user, profile } = useAuth();
  const [data, setData] = useState(INITIAL_DATA);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [notificationRegistered, setNotificationRegistered] = useState(false);
  const [notificationBusy, setNotificationBusy] = useState(false);
  const [notificationPromptOpen, setNotificationPromptOpen] = useState(false);
  const [notificationNotice, setNotificationNotice] = useState('');

  useEffect(() => {
  let active = true;

  if (!user?.uid || !profile) {
    return undefined;
  }

  const roles = currentRoles(profile);

  // Le popup concerne uniquement le gérant.
  if (!roles.includes('gerant')) {
    return undefined;
  }

  hasRegisteredNotificationToken({ user, db })
    .then((registered) => {
      if (!active) return;

      setNotificationRegistered(registered);

      if (registered) {
        setNotificationPromptOpen(false);
        return;
      }

      const snoozeKey =
        `le-defi:gerant-notification-prompt-snooze:${user.uid}`;

      const snoozeUntil = Number(
        localStorage.getItem(snoozeKey) || 0
      );

      if (Date.now() >= snoozeUntil) {
        setNotificationPromptOpen(true);
      }
    })
    .catch((error) => {
      console.error(
        'Dashboard — vérification notifications :',
        error
      );
    });

  return () => {
    active = false;
  };
}, [user?.uid, profile]);

  useEffect(() => {
  if (!user?.uid || !profile) return undefined;

  const roles = currentRoles(profile);
  const isAgent =
    roles.includes('agent_ecole') ||
    roles.includes('agent_college');

  const sources = [
    ['registrations', collection(db, 'demandes_inscription')],
    ['complaints', collection(db, 'reclamations')],
    ['parents', collection(db, 'parents')],
    [
      'tasks',
      isAgent
        ? query(
            collection(db, 'tasks'),
            where('assignedToUid', '==', user.uid)
          )
        : collection(db, 'tasks')
    ]
  ];

  const loaded = new Set();

  const unsubscribers = sources.map(([key, source]) =>
    onSnapshot(
      source,
      (snapshot) => {
        const rows = snapshot.docs
          .map((document) => ({
            id: document.id,
            ...document.data()
          }))
          .sort(
            (a, b) =>
              timestampValue(b.createdAt) -
              timestampValue(a.createdAt)
          );

        setData((current) => ({
          ...current,
          [key]: rows
        }));

        setErrors((current) =>
          current.filter((name) => name !== key)
        );

        loaded.add(key);

        if (loaded.size === sources.length) {
          setLoading(false);
        }
      },
      (error) => {
        console.error(`Dashboard — ${key}:`, error);

        setErrors((current) =>
          current.includes(key)
            ? current
            : [...current, key]
        );

        loaded.add(key);

        if (loaded.size === sources.length) {
          setLoading(false);
        }
      }
    )
  );

  return () => {
    unsubscribers.forEach((unsubscribe) => unsubscribe());
  };
}, [user?.uid, profile]);

async function enableManagerNotifications() {
  if (notificationBusy) return;

  setNotificationBusy(true);
  setNotificationNotice('');

  try {
    const result = await requestBrowserNotifications({
      user,
      profile,
      db
    });

    setNotificationRegistered(result.registered === true);
    setNotificationNotice(result.message || '');

    if (result.registered === true) {
      setNotificationPromptOpen(false);
    }
  } catch (error) {
    console.error(
      'Dashboard — activation notifications :',
      error
    );

    setNotificationNotice(
      'Impossible d’activer les notifications pour le moment.'
    );
  } finally {
    setNotificationBusy(false);
  }
}

function remindNotificationsLater() {
  const snoozeKey =
    `le-defi:gerant-notification-prompt-snooze:${user.uid}`;

  // Réafficher dans 3 jours
  localStorage.setItem(
    snoozeKey,
    String(Date.now() + 3 * 24 * 60 * 60 * 1000)
  );

  setNotificationPromptOpen(false);
}
    

  const firstName = useMemo(() => profile?.fullname?.trim().split(/\s+/)[0] || 'Administration', [profile?.fullname]);
  const roles = currentRoles(profile);
  const isAgent = roles.includes('agent_ecole') || roles.includes('agent_college');
  return <>
  {notificationPromptOpen && (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      background: 'rgba(15, 23, 42, 0.55)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20
    }}
  >
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 440,
        background: '#fff',
        borderRadius: 24,
        padding: 30,
        boxShadow: '0 25px 70px rgba(15, 23, 42, 0.25)'
      }}
    >
      <button
        type="button"
        onClick={remindNotificationsLater}
        aria-label="Fermer"
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          border: 0,
          background: '#f1f5f9',
          width: 36,
          height: 36,
          borderRadius: 12,
          display: 'grid',
          placeItems: 'center',
          cursor: 'pointer'
        }}
      >
        <X size={18} />
      </button>

      <div
        style={{
          width: 58,
          height: 58,
          borderRadius: 18,
          background: '#eff6ff',
          color: '#2563eb',
          display: 'grid',
          placeItems: 'center',
          marginBottom: 20
        }}
      >
        <Bell size={28} />
      </div>

      <p className="eyebrow">NOTIFICATIONS</p>

      <h2 style={{ margin: '6px 0 10px' }}>
        Ne manquez aucune action importante
      </h2>

      <p
        style={{
          color: '#64748b',
          lineHeight: 1.6,
          marginBottom: 22
        }}
      >
        Activez les notifications pour être prévenu lorsqu’un
        enseignant envoie un travail à la maison à valider ou
        lorsqu’une nouvelle action nécessite votre attention.
      </p>

      {notificationNotice && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 12,
            background: '#f8fafc',
            marginBottom: 14,
            fontSize: 14
          }}
        >
          {notificationNotice}
        </div>
      )}

      <button
        type="button"
        className="button button-primary"
        onClick={enableManagerNotifications}
        disabled={notificationBusy || notificationRegistered}
        style={{
          width: '100%',
          justifyContent: 'center',
          marginBottom: 10
        }}
      >
        <Bell size={17} />
        {notificationBusy
          ? 'Activation…'
          : notificationRegistered
            ? 'Notifications activées'
            : 'Activer les notifications'}
      </button>

      <button
        type="button"
        className="button button-secondary"
        onClick={remindNotificationsLater}
        style={{
          width: '100%',
          justifyContent: 'center'
        }}
      >
        Plus tard
      </button>
    </div>
  </div>
)}
    {errors.length > 0 && <div className="dashboard-alert"><AlertCircle size={19}/><div><strong>Certaines données n’ont pas pu être chargées.</strong><span>Certaines sources de données sont momentanément indisponibles : {errors.join(', ')}.</span></div></div>}
    {isAgent
      ? <AgentDashboard data={data} loading={loading} firstName={firstName} userId={user?.uid}/>
      : <ManagerDashboard data={data} loading={loading} firstName={firstName}/>
    }
  </>;
}
